import { useCallback, useRef } from "react";
import { electronModule } from "@/modules/electron";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { useAppStore } from "@/store";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { RecallMeetingEventKind, type RecallMeetingEventPayload } from "@/modules/electron/types";
import { EventContext } from "@/domains/metrics/context";
import { SystemPermissionsModal } from "@/store/system-permissions";
import { DateTime } from "luxon";
import { windowModule } from "@/modules/window";
import { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import { createNoteEncodedContentForAutoStartVoice } from "@/store/note/helpers/auto-start-voice";
import {
  recordingWindowForMeetingEvent,
  type RecordingWindow,
} from "@/store/audio/recording/recording-window";
import { TrashRecordingReason } from "@/store/audio/audio.types";
import { CreateNoteSourceOperation } from "@/store/sync/operations/sources/CreateNoteSourceOperation";
import { resolveNoteSourceSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteSourceSyncModelUuid";
import { resolveSourceIdForCalendarEvent } from "@/modules/uuid/sync-models/resolveSourceIdForCalendarEvent";

const MEETING_HANDOFF_INITIALIZATION_MAX_WAIT_MS = 5_000;
const MEETING_HANDOFF_INITIALIZATION_POLL_MS = 50;

const waitForRecordingInitializationToFinish = async (
  isInitializing: () => boolean
): Promise<boolean> => {
  const startedAt = Date.now();

  while (isInitializing()) {
    if (Date.now() - startedAt >= MEETING_HANDOFF_INITIALIZATION_MAX_WAIT_MS) {
      return false;
    }

    await new Promise(resolve => setTimeout(resolve, MEETING_HANDOFF_INITIALIZATION_POLL_MS));
  }

  return true;
};

export const MEETING_AUTO_START_VOICE_THRESHOLD_MINUTES = 5;

export const shouldAutoStartVoiceForMeetingStartTime = ({
  startTime,
  now = DateTime.now(),
}: {
  startTime?: string | null;
  now?: DateTime;
}): boolean => {
  if (!startTime) return true;
  return (
    DateTime.fromISO(startTime).diff(now, "minutes").minutes <=
    MEETING_AUTO_START_VOICE_THRESHOLD_MINUTES
  );
};

/**
 * Identifies Recall events that should make a detected meeting available for
 * synchronous top-bar voice starts.
 */
const isDetectedMeetingWindowStartKind = (kind: RecallMeetingEventKind): boolean => {
  return (
    kind === RecallMeetingEventKind.MeetingDetected ||
    kind === RecallMeetingEventKind.MeetingUpdated
  );
};

/**
 * Identifies Recall events that should clear the renderer's latest meeting id.
 */
const isDetectedMeetingWindowEndKind = (kind: RecallMeetingEventKind): boolean => {
  return kind === RecallMeetingEventKind.MeetingClosed;
};

type DetectedMeetingWindowCacheUpdate =
  | { action: "set"; windowId: string; calendarEventId: string | null }
  | { action: "clear"; windowId: string };

/**
 * Decides how a Recall meeting lifecycle event should update the renderer cache.
 *
 * Calendar-only meeting notifications use synthetic whole-desktop windows, so
 * they must not make top-bar voice starts target a Recall meeting window.
 */
export const getDetectedMeetingWindowCacheUpdateForMeetingEvent = ({
  kind,
  wasMeetingDetected,
  recordingWindow,
  windowId,
  calendarEventId,
}: {
  kind: RecallMeetingEventKind;
  wasMeetingDetected: boolean;
  recordingWindow: RecordingWindow;
  windowId: string;
  calendarEventId?: string | null;
}): DetectedMeetingWindowCacheUpdate | null => {
  if (!wasMeetingDetected || recordingWindow.kind !== "detected-meeting") {
    return null;
  }

  if (isDetectedMeetingWindowStartKind(kind)) {
    return { action: "set", windowId, calendarEventId: calendarEventId ?? null };
  }

  if (isDetectedMeetingWindowEndKind(kind)) {
    return { action: "clear", windowId };
  }

  return null;
};

export const useSystemAudioPermissionSynchronizer = (): void => {
  const { store } = useAppStore();

  const syncPermissions = useCallback(async () => {
    try {
      const [
        permissionMap,
        { enabled: teamsMeetingDetectionEnabled },
        { enabled: meetingNotificationEnabled },
        { enabled: calendarNotificationEnabled },
      ] = await Promise.all([
        electronModule.recall.getPermissionStatus(),
        electronModule.getTeamsMeetingDetectionEnabled(),
        electronModule.getMeetingNotificationEnabled(),
        electronModule.getCalendarNotificationEnabled(),
      ]);

      logger.info({
        message: "[SystemAudioPermissionSynchronizer] Syncing",
        info: { teamsMeetingDetectionEnabled, permissionMap },
      });

      store.systemPermissions.setPermissionMap({ permissionMap });
      store.systemPermissions.setTeamsMeetingDetectionEnabled({
        enabled: teamsMeetingDetectionEnabled,
      });
      store.systemPermissions.setMeetingNotificationEnabled({
        enabled: meetingNotificationEnabled,
      });
      store.systemPermissions.setCalendarNotificationEnabled({
        enabled: calendarNotificationEnabled,
      });

      const { ui } = store.systemPermissions;

      if (!ui.activeModal) {
        return;
      }

      if (ui.activeModal === SystemPermissionsModal.Voice) {
        if (store.systemPermissions.hasRequiredVoicePermissions) {
          ui.closeModal();
        }
      }

      if (ui.activeModal === SystemPermissionsModal.Meeting) {
        if (store.systemPermissions.hasRequiredMeetingPermissions) {
          ui.closeModal();
        }
      }

      if (ui.activeModal === SystemPermissionsModal.AdvancedMeeting) {
        if (store.systemPermissions.hasRequiredAdvancedMeetingPermissions) {
          ui.closeModal();
        }
      }
    } catch (error) {
      logger.error({
        message: "[SystemAudioPermissionSynchronizer] Failed",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }
  }, [store.systemPermissions]);

  /**
   * Initialize permission state and subscribe to changes.
   */
  useEffectOnMount(() => {
    if (!store.interface.isDesktop) {
      return;
    }

    if (store.electron.isIntelMacDesktop) {
      /**
       * This feature isn't currently supported on Intel-based MacOS devices.
       */
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const asyncLogic = async () => {
      await syncPermissions();

      try {
        unsubscribe = await electronModule.recall.onPermissionStatus?.({
          callback: () => {
            void syncPermissions();
          },
        });
      } catch (_) {
        /* empty */
      }
    };

    void asyncLogic();

    return () => {
      try {
        unsubscribe?.();
      } catch (_) {
        /* empty */
      }
    };
  });
};

/**
 * Listens to meeting detection events and handles them by either joining an ongoing recording
 * or creating a new note with the detected meeting window.
 */
export const useMeetingDetectionListener = (): void => {
  const { store } = useAppStore();
  const advancedMeetingPermissionShownRef = useRef(false);
  const meetingHandoffWindowIdsRef = useRef(new Set<string>());

  const handleMeetingDetected = useCallback(
    async ({ event, kind, debug, wasMeetingDetected }: RecallMeetingEventPayload) => {
      const { window, info } = event;
      // The single point where a meeting event becomes a recording window.
      // Calendar-only notifications (wasMeetingDetected=false) carry a
      // synthetic window id and resolve to whole-desktop recording.
      const recordingWindow = recordingWindowForMeetingEvent({
        wasMeetingDetected,
        windowId: window.id,
      });

      const hasFocus = document.hasFocus();
      logger.info({
        message: `[${kind}] Meeting event received (${window.id})`,
        info: {
          debug,
          wasMeetingDetected,
          window,
          info: info ?? null,
          kind,
          visibilityState: document.visibilityState,
          hidden: document.hidden,
          hasFocus,
        },
      });

      const detectedMeetingWindowCacheUpdate = getDetectedMeetingWindowCacheUpdateForMeetingEvent({
        kind,
        wasMeetingDetected,
        recordingWindow,
        windowId: window.id,
        calendarEventId: info?.calendarEventId ?? null,
      });
      if (detectedMeetingWindowCacheUpdate?.action === "set") {
        store.electron.setLatestDetectedMeetingWindow({
          windowId: detectedMeetingWindowCacheUpdate.windowId,
          calendarEventId: detectedMeetingWindowCacheUpdate.calendarEventId,
        });
      } else if (detectedMeetingWindowCacheUpdate?.action === "clear") {
        store.electron.clearLatestDetectedMeetingWindow({
          windowId: detectedMeetingWindowCacheUpdate.windowId,
        });
      }

      const handleDetectedMeetingHandoff = async (): Promise<boolean> => {
        if (!isDetectedMeetingWindowStartKind(kind)) {
          return false;
        }

        if (store.audio.recording.isInitializing) {
          const didFinishInitializing = await waitForRecordingInitializationToFinish(
            () => store.audio.recording.isInitializing
          );

          if (!didFinishInitializing) {
            return true;
          }
        }

        const activeRecordingId = store.audio.activeRecordingId;
        if (!activeRecordingId) {
          return false;
        }

        if (recordingWindow.kind !== "detected-meeting") {
          return true;
        }

        // This is false when the current recording already came from meeting
        // detection, where speaker attribution should already be available.
        if (
          !store.audio.recording.canReplaceWithDetectedMeetingWindow({
            audioRecordingId: activeRecordingId,
          })
        ) {
          return true;
        }

        if (meetingHandoffWindowIdsRef.current.has(window.id)) {
          return true;
        }

        meetingHandoffWindowIdsRef.current.add(window.id);
        try {
          const replacement = await store.audio.replaceActiveRecordingWithDetectedMeetingWindow({
            meetingEventWindowId: window.id,
            recordingWindow,
            calendarEventId: info?.calendarEventId ?? null,
          });
          if (!replacement) {
            meetingHandoffWindowIdsRef.current.delete(window.id);
          }
        } catch (error) {
          meetingHandoffWindowIdsRef.current.delete(window.id);
          logger.warn({
            message: "[MeetingDetectionListener] Failed to hand off active recording",
            info: {
              windowId: window.id,
              error: objectModule.safeErrorAsJson(error as Error),
            },
          });
        }

        return true;
      };

      if (debug) {
        store.electron.pushRecallDebugEvent(window, kind);
        if (kind === RecallMeetingEventKind.MeetingDetected) {
          if (
            event.window.platform === "teams" &&
            !store.systemPermissions.hasRequiredAdvancedMeetingPermissions &&
            !advancedMeetingPermissionShownRef.current
          ) {
            store.systemPermissions.ui.openAdvancedMeetingPermissionsModal();
            electronModule.setAdvancedMeetingDetectionShown({ shown: true });
            advancedMeetingPermissionShownRef.current = true;
            return;
          }

          if (await handleDetectedMeetingHandoff()) {
            return;
          }

          // Return early if it's not idle.
          if (store.audio.activeRecordingId || store.audio.recording.isInitializing) {
            return;
          }

          // Make sure prepared assets are warm so that a click on the
          // notification starts recording instantly. The pool slot is
          // window-agnostic, so nothing needs to be correlated to this
          // particular meeting.
          store.audio.recording.preparedRecordingPool.ensureWarm("meeting-detected");
        }
        return;
      }

      if (kind === RecallMeetingEventKind.MeetingClosed) {
        const activeAudioRecordingId = store.audio.activeRecordingId;

        const associatedNoteId = store.audio.activeRecordingNoteId;

        if (document.visibilityState === "hidden" || document.hidden) {
          logger.info({
            message:
              "[MeetingDetectionListener] Ignoring meeting closed event because page is hidden",
            info: {
              visibilityState: document.visibilityState,
              hidden: document.hidden,
              hasFocus,
              activeAudioRecordingId,
              associatedNoteId: associatedNoteId ?? null,
            },
          });
          return;
        }

        if (associatedNoteId) {
          store.navigation.goToNote({ noteId: associatedNoteId });
        }

        if (!activeAudioRecordingId) return;

        store.audio.finishRecording({
          audioRecordingId: activeAudioRecordingId,
        });
        return;
      }

      if (await handleDetectedMeetingHandoff()) {
        return;
      }

      const shouldAutoStartVoice = shouldAutoStartVoiceForMeetingStartTime({
        startTime: info?.startTime,
      });

      let startPromise: Promise<{ audioRecordingId: Uuid } | null> | null = null;

      const startRecording = (noteId: Uuid) => {
        logger.info({
          message: "[MeetingDetectionListener] Auto-starting voice recording?",
          info: {
            noteId,
            window,
            info: info ?? null,
            calendarEventId: info?.calendarEventId ?? null,
            recordingWindow,
            autoStartVoice: shouldAutoStartVoice,
          },
        });

        if (window?.url) {
          void windowModule.openInNewTab({ url: new URL(window.url) });
        }

        if (!shouldAutoStartVoice) return;

        store.electron.pushRecallDebugEvent(window, "auto-start-voice");

        startPromise = store.audio
          .createAndStartAudioRecording({
            expandVoicePopover: false,
            associatedNoteId: noteId,
            meetingEventWindowId: window.id,
            recordingWindow,
            calendarEventId: info?.calendarEventId ?? undefined,
          })
          .catch(() => null);
      };

      const onCreateError = () => {
        // Trash the recording only if the start above actually created one.
        void startPromise?.then(result => {
          if (!result) return;
          return store.audio.trashRecording({
            audioRecordingId: result.audioRecordingId,
            context: { reason: TrashRecordingReason.CreateNoteFailed },
          });
        });
      };

      const calendarEvent = info?.calendarEventId
        ? await store.spaceAccountCalendarEvents.getAsync(info?.calendarEventId)
        : undefined;

      const associatedNote = await (async () => {
        if (!calendarEvent) return;

        const noteId = calendarEvent?.associatedNote?.id;
        if (!noteId) return;

        return store.notes.getAsync(noteId);
      })();

      // If it's changed we don't want to insert the template for calendar event notes.
      if (associatedNote && !associatedNote.isNoteContentInOriginalState) {
        startRecording(associatedNote.id);
        store.navigation.goToNote({ noteId: associatedNote.id });
        return;
      }

      // Create a new note with the detected meeting window as autoStartVoice
      const noteId = associatedNote?.id ?? uuidModule.generate();
      store.notes.composeNewNote({
        eventContext: EventContext.MeetingDetectionNotification,
        calendarEvent,
        noteContent: createNoteEncodedContentForAutoStartVoice({
          windowId: window.id,
          windowTitle: window.title,
          windowUrl: window.url,
          platform: window.platform,
          startTime: info?.startTime ?? undefined,
          endTime: info?.endTime ?? undefined,
          attendees: info?.attendees ?? [],
        }),
        noteId,
        autoFocusPosition: "end",
        onBeforeCreate: async () => {
          if (calendarEvent) {
            const sourceId = resolveSourceIdForCalendarEvent({
              connectedCalendarEventId: calendarEvent!.associatedConnectedCalendarEventId,
            });
            const noteSourceId = resolveNoteSourceSyncModelUuid({
              noteId,
              sourceId,
            });
            // Avoid race condition between ensureSourceForRecording and
            // CreateNoteUsingCalendarEventOperation.
            await new CreateNoteSourceOperation({
              store: store,
              payload: {
                id: noteSourceId,
                schema_version: 1,
                note_id: noteId,
                source_id: sourceId,
              },
            }).execute();
          }
          startRecording(noteId);
        },
        onCreateError,
      });
    },
    [store]
  );

  useEffectOnMount(() => {
    if (!store.interface.isDesktop) {
      return;
    }

    if (store.electron.isIntelMacDesktop) {
      /**
       * This feature isn't currently supported on Intel-based MacOS devices.
       */
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const asyncLogic = async () => {
      electronModule.setIsClientReady({ isReady: true });

      try {
        const { shown: advancedMeetingDetectionShown } =
          await electronModule.getAdvancedMeetingDetectionShown();
        advancedMeetingPermissionShownRef.current = advancedMeetingDetectionShown;
      } catch (error) {
        logger.error({
          message: "Failed to get advanced meeting detection shown",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      }

      try {
        unsubscribe = await electronModule.recall.onMeetingEvent?.({
          callback: handleMeetingDetected,
        });
      } catch (error) {
        logger.error({
          message: "Failed to subscribe to meeting detection events",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      }
    };

    void asyncLogic();

    return () => {
      electronModule.setIsClientReady({ isReady: false });
      try {
        unsubscribe?.();
      } catch (_) {
        /* empty */
      }
    };
  });
};
